import { ref, computed } from "vue";

const TAB_NAME = "noa-recorder-tab";

const tabRef = ref<Window | null>(null);
const tabClosedUnexpectedly = ref(false);
let pollingId: ReturnType<typeof setInterval> | null = null;
let focusHandler: (() => void) | null = null;

const isTabOpen = computed(() => Boolean(tabRef.value && !tabRef.value.closed));

function openTab(url: string): boolean {
  const tab = window.open(url, TAB_NAME);
  if (!tab) return false;
  tabRef.value = tab;
  tabClosedUnexpectedly.value = false;
  startPolling();
  return true;
}

function focusTab() {
  if (tabRef.value && !tabRef.value.closed) {
    tabRef.value.focus();
  }
}

/**
 * Re-attach to the named recorder tab after page reload.
 * Uses window.open("", name): existing tab → returns proxy silently (no popup, no focus theft).
 * Closed tab → browser tries to create blank tab → popup blocked → null ≡ tab gone.
 *
 * Because the proxy retrieved this way may not update .closed reliably (COOP-isolated tabs),
 * also registers a focus listener on the parent window. When the user returns to this page
 * (e.g. after closing the recorder tab), the check runs once without continuous focus theft.
 */
function watchNamedTab(): boolean {
  const probe = window.open("", TAB_NAME);
  if (!probe) {
    tabClosedUnexpectedly.value = true;
    return false;
  }
  try {
    const href = probe.location.href;
    if (!href || href === "about:blank") {
      probe.close();
      tabClosedUnexpectedly.value = true;
      return false;
    }
    tabRef.value = probe;
    startPolling();
    startFocusCheck();
    return true;
  } catch {
    // Cross-origin SecurityError: recorder tab is loaded and alive
    tabRef.value = probe;
    startPolling();
    startFocusCheck();
    return true;
  }
}

/**
 * Try to focus the named recorder tab without opening a new one.
 * Used on session recovery when the tab ref is lost (page reload).
 * Returns true if the tab was found and focused, false otherwise.
 */
function tryFocusNamedTab(): boolean {
  const probe = window.open("", TAB_NAME);
  if (!probe) return false;
  try {
    const href = probe.location.href;
    // New blank tab (tab was closed) — close it immediately and give up
    if (!href || href === "about:blank") {
      probe.close();
      tabClosedUnexpectedly.value = true;
      return false;
    }
    // Same-origin tab with content — focus it
    probe.focus();
    tabRef.value = probe;
    startPolling();
    return true;
  } catch {
    // Cross-origin SecurityError means the tab has the recorder URL loaded — focus it
    probe.focus();
    tabRef.value = probe;
    startPolling();
    return true;
  }
}

function clearTab() {
  stopPolling();
  stopFocusCheck();
  tabRef.value = null;
  tabClosedUnexpectedly.value = false;
}

function startPolling() {
  stopPolling();
  pollingId = setInterval(() => {
    if (tabRef.value?.closed) {
      stopPolling();
      tabRef.value = null;
      tabClosedUnexpectedly.value = true;
    }
  }, 1000);
}

function stopPolling() {
  if (pollingId !== null) {
    clearInterval(pollingId);
    pollingId = null;
  }
}

/**
 * Registers a one-shot focus check: when the parent window regains focus (user switches back
 * to this tab, typically after closing the recorder tab), probe the named tab once.
 * window.open("", name) here does NOT steal focus because this page already has focus.
 * If null is returned (popup blocked = tab gone), signal closure immediately.
 */
function startFocusCheck() {
  stopFocusCheck();
  focusHandler = () => {
    if (!tabRef.value) return;
    const probe = window.open("", TAB_NAME);
    if (!probe) {
      stopPolling();
      stopFocusCheck();
      tabRef.value = null;
      tabClosedUnexpectedly.value = true;
    }
  };
  window.addEventListener("focus", focusHandler);
}

function stopFocusCheck() {
  if (focusHandler) {
    window.removeEventListener("focus", focusHandler);
    focusHandler = null;
  }
}

export function useRecorderTab() {
  return { isTabOpen, tabClosedUnexpectedly, openTab, focusTab, watchNamedTab, tryFocusNamedTab, clearTab };
}
